import React, { useEffect, useMemo, useState } from "react";
import { Table, Typography, Spin, Modal, Space, Button, Select, Descriptions, Tooltip } from "antd";
import { InfoCircleOutlined, PlusOutlined, FolderAddOutlined, EditOutlined, DeleteOutlined, WarningOutlined } from "@ant-design/icons";
import ExpandCollapseAllButton from "../../common/ExpandCollapseAllButton";
import NumberInput from "../../common/NumberInput";
import BcCalculationDetails, { computeBcCalculation, SummaryKeyRawFields } from "../../common/BcCalculationDetails";
import { useTender } from "../../../contexts/TenderContext";
import { useDevMode } from "@/contexts/DevModeContext";
import { useCanConfigureBoq } from "@/hooks/useCanConfigureBoq";
import { generateBOQSummaryWithCategories } from "../../../utils/boqSummaryGenerator";
import { COLORS, formatUnit, MAX_SECTION_NESTING_DEPTH, MAX_SECTION_NESTING_DEPTH_MESSAGE, buildSectionDepthMap } from "../../../utils/constants";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import { useGlobalSettings } from "../../../hooks/useGlobalSettings";
import { createPlotPavingsRateLookupMap, applyPlotPavingsRatesToSummary, PLOT_PAVINGS_M3_CONVERSION_FIELDS } from "../../../utils/plotPavingsItemLibraryIntegration";
import { PLOT_PAVINGS_CATEGORIES } from "../../../utils/billSummaryCategories";
import { reconstructPlotPavingsColumnHeaders } from "../../../utils/plotPavingsColumnMapping";
import { getPlotPavingsConfigureHeadersViolationsByField, type FieldConfigureHeadersViolation } from "../../../utils/groundworksConfigureHeadersValidation";
import { useBoqLatestVersion } from "../../../hooks/useBillOfQuantities";
import { BoqSummaryLineItem } from "../../../utils/groundworksBoqSummaryComputer";
import { computeDynamicBcCalculation, type DynamicBillSummaryLineItem } from '../../../utils/dynamicBillSummarySerialization';
import { useBillSummaryExtraItems, buildRootExtraRows, buildNestedExtraRows } from '../../../hooks/useBillSummaryExtraItems';
import { generatePlotMatrixCategoryColumns } from '../../../utils/plotMatrixCategoryGenerator';
import type { DynamicBillSummaryNode, DynamicBillSummaryItemNode, DynamicBillSummarySectionNode } from '../../../types/tender.types';

const findExtraNode = (nodes: DynamicBillSummaryNode[], id: string): DynamicBillSummaryNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.type === "section") {
      const found = findExtraNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

const { Text, Title } = Typography;

// Header/section rowTypes across this table — 'sub-header' is the only one
// the FIXED data ever produces; 'main-header'/'column-type-header' only ever
// come from ad-hoc extra Section rows (root-level / nested respectively —
// see useBillSummaryExtraItems.tsx's buildExtraRow depth convention), but
// every column below still has to treat all three the same way so a
// newly-added extra section renders consistently instead of falling through
// to leaf-row rendering.
const isHeaderRowType = (rowType: string) =>
  rowType === "sub-header" || rowType === "main-header" || rowType === "column-type-header";

function computePlotPavingsDbRate(
  fieldName: string,
  rate: number,
  columnHeaders?: Record<string, string>,
): number {
  if (!PLOT_PAVINGS_M3_CONVERSION_FIELDS.has(fieldName) || !columnHeaders) return rate;
  const raw = columnHeaders[fieldName];
  if (!raw) return rate;
  const match = raw.trim().match(/^(\d+(?:\.\d+)?)/);
  const value = match ? parseFloat(match[1]) : null;
  if (!value || value <= 0) return rate;
  // value >= 1 → entered as mm (e.g. "150") → convert to metres
  // value < 1  → already metres (e.g. "0.150")
  const mult = value >= 1 ? value / 1000 : value;
  return rate / mult;
}

interface PlotPavingsSummaryItem {
  key: string;
  itemNumber: string;
  fieldName: string;
  description: string;
  quantity: number;
  unit: string;
  rate: number;
  amount: number;
  category: string;
  resourceCode?: string;
}

const PlotPavingsBillSummary: React.FC = () => {
  const showDevMode = useDevMode();
  // Ad-hoc "Add Item"/"Add Section" extra rows — gated by the "Configure BOQ" switch (next to the Re-calculate
  // button on /groundworks/review), independent of Dev Mode (useCanConfigureBoq(), hooks/useCanConfigureBoq.ts) — same
  // convention as the other groundworks Bill Summaries.
  const dynamicSummaryEnabled = useCanConfigureBoq();
  const { state, updateField } = useTender();
  const { settings } = useGlobalSettings();
  const { data: itemLibraryData, isLoading: isLoadingItemLibrary } = useItemLibrary(state.region || "");
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [summaryKeyModalRecord, setSummaryKeyModalRecord] = useState<BoqSummaryLineItem | null>(null);
  const [configViolationModalRecord, setConfigViolationModalRecord] = useState<FieldConfigureHeadersViolation | null>(null);
  const configViolationsByField = useMemo(
    () => getPlotPavingsConfigureHeadersViolationsByField(state.groundworks),
    [state.groundworks],
  );
  // SummaryKey modal for ad-hoc extra items — these have no persisted boq-versions
  // entry (`apiSummaryItem`) since they aren't serialised into the exported BOQ
  // payload yet, so this shows the live-derived value instead, same "unsaved"
  // convention DynamicBillSummaryPanel's own SummaryKey column uses.
  // `persisted` (when set) is the actual raw `BoqSummaryLineItem` fetched from the
  // BOQ API for this extra item's own `fieldName` — shown in full via
  // `SummaryKeyRawFields` so the modal reflects every detail the API actually
  // has, not just the hand-picked subset of `live`'s fields. `live` (the
  // client-side node, always set) still drives the BCCalculation breakdown,
  // since that derivation needs the node's own `resourceCodes` array — a
  // persisted record only ever carries the combined `resourceCode` string.
  const [extraSummaryKeyRecord, setExtraSummaryKeyRecord] = useState<{
    live: DynamicBillSummaryLineItem;
    persisted: BoqSummaryLineItem | null;
  } | null>(null);

  // Ad-hoc user-added Section/Item rows grafted onto this otherwise-immutable
  // computed table — see useBillSummaryExtraItems.tsx. Gated by
  // dynamicSummaryEnabled (the "Configure BOQ" switch), same as every other
  // groundworks Bill Summary's own extra items. `plotPavingsExtraItems` is
  // keyed by "attachment point": `"__root__"` for new top-level rows, or an
  // existing computed category row's own `key` for rows grafted onto it.
  const plotPavingsExtraItems = state.groundworks?.plotPavingsExtraItems;
  // Live Plot Matrix columns, for the Add/Edit Item modal's "Include in Plot
  // Matrix" Valuation Stage lookup — same call GroundworksCutFillBillSummary.tsx
  // makes for its own extra items.
  const extraItemsPlotMatrixColumns = useMemo(
    () => generatePlotMatrixCategoryColumns(state.groundworks as any),
    [state.groundworks],
  );
  // Newly-added extra row (Section or Item) — auto-expands the row it was
  // grafted onto and briefly highlights/autofocuses it, same convention
  // GroundworksCutFillBillSummary.tsx follows for its own Add Section/Add Item.
  const [newlyAddedRowId, setNewlyAddedRowId] = useState<string | null>(null);
  useEffect(() => {
    if (!newlyAddedRowId) return;
    const timer = setTimeout(() => setNewlyAddedRowId(null), 2000);
    return () => clearTimeout(timer);
  }, [newlyAddedRowId]);

  const extraItemsApi = useBillSummaryExtraItems({
    extraItems: plotPavingsExtraItems,
    onChange: (updated) =>
      updateField("groundworks", { ...state.groundworks, plotPavingsExtraItems: updated }),
    itemLibraryData,
    plotMatrixColumns: extraItemsPlotMatrixColumns,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) {
        setExpandedRowKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      }
      setNewlyAddedRowId(nodeId);
    },
  });

  // Dev-mode only: pulls the persisted `groundworks.summary.plotPavings` line items from the
  // same boq-versions API (`getBoqVersion`) that drives the rest of the review page, so the
  // SummaryKey column reflects what was actually saved rather than the live recomputation above.
  const { data: latestBoqVersionData } = useBoqLatestVersion(
    state.projectName || undefined,
    { enabled: showDevMode && !!state.projectName, oppNo: state.opportunityNo },
  );

  const apiPlotPavingsSummaryByFieldName = useMemo(() => {
    const map = new Map<string, BoqSummaryLineItem>();
    const items = (latestBoqVersionData as unknown as { boq?: { billOfQuantities?: Array<{ groundworks?: { summary?: { plotPavings?: BoqSummaryLineItem[] } } }> } })
      ?.boq?.billOfQuantities?.[0]?.groundworks?.summary?.plotPavings;
    (items || []).forEach((item) => {
      if (item.fieldName) map.set(item.fieldName, item);
    });
    return map;
  }, [latestBoqVersionData]);

  // Create rate lookup map from item library
  const rateLookupMap = useMemo(() => {
    if (!itemLibraryData) {
      return new Map();
    }

    // Pass groundworks data for dynamic field codes (user selections like asphalt types)
    return createPlotPavingsRateLookupMap(itemLibraryData, state.groundworks);
  }, [itemLibraryData, state.groundworks]);

  // Build a code→{rate} lookup directly from item library, for the SummaryKey modal's
  // "BCCalculation" line (same convention as GroundworksCutFillBillSummary).
  const codeRateLookup = useMemo(() => {
    const map = new Map<string, { rate: number }>();
    if (!itemLibraryData) return map;
    itemLibraryData.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) {
        map.set(code, { rate: item.nettRate || item.baseRate || 0 });
      }
    });
    return map;
  }, [itemLibraryData]);

  // Generate dynamic summary from actual Plot Pavings data
  const summaryData = useMemo(() => {
    if (!state.groundworks) {
      return [];
    }

    // Build dimension values and reconstructed column headers
    const dimensionValues = state.groundworks?.plotPavingsDimensionValues;
    const columnHeaders = reconstructPlotPavingsColumnHeaders(
      state.groundworks?.plotPavingsColumnHeaders || {},
      dimensionValues || {}
    );

    // Generate base summary with quantities
    const baseItems = generateBOQSummaryWithCategories(
      state.groundworks,
      'Plot Pavings',
      PLOT_PAVINGS_CATEGORIES,
      dimensionValues,
      null,
      columnHeaders
    );

    // Apply rates from item library
    const itemsWithRates = applyPlotPavingsRatesToSummary(baseItems, rateLookupMap);

    // Filter out zero-quantity items
    const filteredItems = itemsWithRates.filter((item) => item.quantity > 0);

    // Add item numbers and convert to the expected format
    return filteredItems.map((item, index) => ({
      key: item.key,
      itemNumber: `${index + 1}.0`,
      fieldName: item.fieldName,
      description: item.description,
      quantity: item.quantity,
      unit: item.unit,
      rate: item.rate,
      amount: item.amount,
      category: item.category,
      resourceCode: (item as any).resourceCode || '',
    } as PlotPavingsSummaryItem));
  }, [state.groundworks, rateLookupMap]);

  const markupFactor = (1 + settings.groundworks.overheadAndProfit / 100) * (1 + settings.groundworks.preliminaries / 100);

  // Process data to create collapsible grouped structure
  const pavingsColHeaders = state.groundworks?.plotPavingsColumnHeaders as Record<string, string> | undefined;
  const rawProcessedData = summaryData.map((item) => {
    const roundedQty = Math.round(item.quantity);
    const markedUpRate = item.rate * markupFactor;
    const displayRate = parseFloat(markedUpRate.toFixed(2));
    return {
      key: item.key,
      category: item.itemNumber,
      description: item.description,
      quantity: String(roundedQty),
      unit: item.unit,
      rate: markedUpRate.toFixed(2),
      amount: Math.abs(roundedQty * displayRate).toFixed(2),
      categoryName: item.category,
      rowType: 'line-item' as const,
      hasData: true,
      isDiameterHeader: false,
      resourceCode: item.resourceCode || '',
      dbRate: computePlotPavingsDbRate(item.fieldName, item.rate, pavingsColHeaders).toFixed(4),
      apiSummaryItem: apiPlotPavingsSummaryByFieldName.get(item.fieldName) || null,
      configViolation: configViolationsByField.get(item.fieldName) ?? null,
    };
  });

  // Create collapsible structure grouped by category
  const groupedProcessedData: any[] = [];
  const categoryGroups = new Map<string, any[]>();

  // Group items by category
  rawProcessedData.forEach((item: any) => {
    if (!categoryGroups.has(item.categoryName)) {
      categoryGroups.set(item.categoryName, []);
    }
    categoryGroups.get(item.categoryName)!.push(item);
  });

  // Create collapsible structure with category headers
  let categoryIndex = 1;
  categoryGroups.forEach((items, categoryName) => {
    // Calculate category total
    const sectionAmount = items.reduce((sum, item) => {
      return sum + parseFloat(item.amount);
    }, 0);

    const categoryChildren: any[] = [];
    items.forEach((item, idx) => {
      categoryChildren.push({
        ...item,
        category: `${categoryIndex}.${idx + 1}`,
      });
    });

    // Add category header with children
    groupedProcessedData.push({
      key: `category-${categoryName}`,
      category: `${categoryIndex}.0`,
      description: categoryName.toUpperCase(),
      quantity: '',
      unit: '',
      rate: '',
      amount: '',
      rowType: 'sub-header',
      isDiameterHeader: true,
      children: categoryChildren,
      sectionAmount,
      categoryName
    });

    categoryIndex++;
  });

  // Graft ad-hoc extra rows on top of this otherwise-immutable computed tree —
  // nested additions merge onto the matching existing category row (by
  // `key`), and root additions become new top-level sections continuing the
  // same whole-number numbering the category rows above use. Same convention
  // GroundworksCutFillBillSummary.tsx / FoundationsBillSummary.tsx follow.
  const extraBuckets = plotPavingsExtraItems ?? {};
  // Buckets whose attach-point row is actually present in this render's
  // computed tree — anything left over (see orphanedExtraRows below) had its
  // row disappear (e.g. the Plot Pavings take-off table was emptied, dropping
  // every category row it was nested under) and must not be silently lost.
  const consumedAttachKeys = new Set<string>();
  const injectExtraRows = (rows: any[]): any[] =>
    rows.map((row) => {
      const bucket = extraBuckets[row.key];
      const existingChildren: any[] = row.children ?? [];
      const ownChildren = row.children ? injectExtraRows(row.children) : undefined;
      if (!bucket || bucket.length === 0) {
        return ownChildren ? { ...row, children: ownChildren } : row;
      }
      consumedAttachKeys.add(row.key);
      const extraRows = buildNestedExtraRows(bucket, row.key, row.category, existingChildren.length, markupFactor);
      const mergedChildren = [...(ownChildren ?? []), ...extraRows];
      return {
        ...row,
        children: mergedChildren,
        ...(row.sectionAmount !== undefined && {
          sectionAmount: mergedChildren.reduce((sum, c) => sum + (parseFloat(c.amount || "0") || 0), 0),
        }),
      };
    });
  const injectedResult = injectExtraRows(groupedProcessedData);
  let nextRootNumber = categoryIndex;
  const rootExtraRows = buildRootExtraRows(extraBuckets["__root__"] ?? [], "__root__", nextRootNumber, markupFactor);
  nextRootNumber += rootExtraRows.length;
  // Orphaned nested buckets (attach-point row no longer exists — e.g. its
  // parent category disappeared) wrap under a single synthetic "Additional
  // Items" header instead of landing as bare unlabelled rows — not a real
  // node itself (Edit/Delete never targets it, only its real children match
  // a node id), just keyed by the REAL attachKey so those children's
  // Edit/Delete/Qty edits still address the correct bucket in state.
  const orphanedExtraRows = Object.keys(extraBuckets)
    .filter((key) => key !== "__root__" && !consumedAttachKeys.has(key) && extraBuckets[key].length > 0)
    .map((key) => {
      const n = nextRootNumber++;
      const children = buildNestedExtraRows(extraBuckets[key], key, `${n}.0`, 0, markupFactor);
      // Category rows are keyed `category-${categoryName}` (see
      // groupedProcessedData above) — recover the original category name
      // straight from the key so the orphaned section keeps its real title
      // instead of a generic placeholder.
      const originalLabel = key.startsWith("category-") ? key.slice("category-".length).toUpperCase() : "Additional Items";
      return {
        key: `orphaned-${key}`,
        category: `${n}.0`,
        description: originalLabel,
        rowType: "main-header",
        isExtra: true,
        attachKey: key,
        nodeType: "section",
        quantity: "",
        unit: "",
        rate: "",
        amount: "",
        dbRate: "",
        sectionAmount: children.reduce((sum, c) => sum + (parseFloat(c.amount || "0") || 0), 0),
        children,
      };
    });

  const processedData: any[] = [...injectedResult, ...rootExtraRows, ...orphanedExtraRows];

  // Depth of every row (real category rows AND grafted extra rows alike) in
  // the merged tree above — gates a section row's "Add Section" Actions icon
  // at MAX_SECTION_NESTING_DEPTH, counting pre-existing real category
  // nesting as well as already-added extra sections.
  const rowDepthMap = buildSectionDepthMap(processedData);

  // Show loading state while item library is loading
  if (isLoadingItemLibrary) {
    return (
      <div style={{ padding: "16px", textAlign: "center" }}>
        <Spin size="large" />
        <div style={{ marginTop: "16px" }}>
          <Text className="text-gray-500">Loading item library rates...</Text>
        </div>
      </div>
    );
  }

  // An empty Plot Pavings take-off table alone must NOT hide already-saved
  // ad-hoc extra items — see GroundworksCutFillBillSummary.tsx's identical fix.
  const hasExtraItems = Object.values(plotPavingsExtraItems ?? {}).some((bucket) => bucket.length > 0);

  return (
    summaryData.length === 0 && !hasExtraItems ? (
      <div style={{ padding: '16px' }}>
        <Text className="text-gray-500">
          No Plot Pavings data available. Please complete the Plot Pavings section first.
        </Text>
      </div>
    ) : (
      <div style={{ padding: '16px' }}>
        {/* Header */}
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={5} style={{ margin: 0, fontWeight: 600, fontSize: '16px', color: '#111827' }}>
            Plot Pavings Bill Summary
          </Title>
          <Space>
            {dynamicSummaryEnabled && !state.isViewMode && (
              <>
                <Button size="small" icon={<FolderAddOutlined />} onClick={() => extraItemsApi.openAddSection("__root__", null)}>
                  Add Section
                </Button>
                {/* Root-level "Add Item" button hidden per request (2026-09-01) — row-level Add Item icons remain.
                <Button size="small" icon={<PlusOutlined />} onClick={() => extraItemsApi.openAddItem("__root__", null)}>
                  Add Item
                </Button>
                */}
              </>
            )}
            <ExpandCollapseAllButton
              expanded={expandedRowKeys.length > 0}
              onToggle={() => {
                if (expandedRowKeys.length > 0) {
                  setExpandedRowKeys([]);
                } else {
                  const allKeys: React.Key[] = [];
                  const collect = (rows: any[]) => {
                    rows.forEach((row) => {
                      if (row.children?.length) {
                        allKeys.push(row.key);
                        collect(row.children);
                      }
                    });
                  };
                  collect(processedData);
                  setExpandedRowKeys(allKeys);
                }
              }}
            />
          </Space>
        </div>

        <style>
          {`
            .plot-pavings-summary-table .ant-table {
              border-radius: 0 !important;
            }
            .plot-pavings-summary-table .ant-table-container {
              border-radius: 0 !important;
            }
          `}
        </style>
        <Table
          className="plot-pavings-summary-table"
        dataSource={processedData}
        expandable={{
          childrenColumnName: 'children',
          defaultExpandAllRows: false,
          expandedRowKeys: expandedRowKeys,
          onExpand: (expanded, record) => {
            if (expanded) {
              setExpandedRowKeys([...expandedRowKeys, record.key]);
            } else {
              setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.key));
            }
          },
        }}
        columns={[
          {
            title: 'Item',
            dataIndex: 'category',
            key: 'category',
            width: 110,
            align: 'center',
            render: (value, record) => {
              if (isHeaderRowType(record.rowType) || record.rowType === 'description-header') {
                return <Text strong>{value}</Text>;
              }
              return value;
            },
          },
          {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: 350,
            render: (value, record) => {
              if (isHeaderRowType(record.rowType) || record.rowType === 'description-header') {
                return <Text strong className="text-indigo-600">{value}</Text>;
              }
              if (record.rowType === 'description') {
                return <Text italic className="text-gray-600">{value}</Text>;
              }
              return (
                <div>
                  <Text>{value}</Text>
                  {record.configViolation && (
                    <Tooltip title="Configure Headers required — click for details">
                      <WarningOutlined
                        style={{ color: '#faad14', marginLeft: 6, cursor: 'pointer' }}
                        onClick={() => setConfigViolationModalRecord(record.configViolation)}
                      />
                    </Tooltip>
                  )}
                  {showDevMode && (record.resourceCode || (record.resourceCodes && record.resourceCodes.length > 0)) && (
                    <div style={{ fontSize: 10, color: '#999', fontWeight: 500, marginTop: 2 }}>
                      {record.resourceCode || record.resourceCodes!.map((c: { resourceCode: string; operator: '+' | '-' }, i: number) => (i === 0 ? c.resourceCode : `${c.operator} ${c.resourceCode}`)).join(' ')}
                    </div>
                  )}
                </div>
              );
            },
          },
          {
            title: 'Qty',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 110,
            align: 'right',
            render: (value, record) => {
              if (isHeaderRowType(record.rowType) || record.rowType === 'description' || record.rowType === 'description-header') {
                return '';
              }
              // Ad-hoc extra items are directly editable in-cell, same as
              // GroundworksCutFillBillSummary.tsx's own Qty column — existing
              // computed rows stay read-only. Text amount type has no Qty at
              // all, same convention DynamicBillSummaryPanel's Qty column follows.
              if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
                return (
                  <NumberInput
                    value={parseFloat(value) || 0}
                    onChange={(qty) => extraItemsApi.updateNode(record.attachKey, record.key, { qty })}
                    size="small"
                    decimalPlaces={2}
                    autoFocus={record.key === newlyAddedRowId}
                  />
                );
              }
              if (!value || value === '' || value === '0' || value === '0.00' || value === '0.000') return '';
              const numValue = typeof value === 'number' ? value : parseFloat(value);
              return isNaN(numValue) ? '' : <div style={{ textAlign: 'right' }}>{Math.round(numValue).toLocaleString('en-GB')}</div>;
            },
          },
          {
            title: 'Unit',
            dataIndex: 'unit',
            key: 'unit',
            width: 110,
            align: 'center',
            render: (value: string, record) => {
              if (isHeaderRowType(record.rowType) || record.rowType === 'description' || record.rowType === 'description-header') {
                return '';
              }
              // Same in-cell Select every extra-items Unit column uses.
              // Text amount type has no Unit at all.
              if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
                return (
                  <Select
                    value={value || undefined}
                    onChange={(unit) => extraItemsApi.updateNode(record.attachKey, record.key, { unit })}
                    options={extraItemsApi.unitOptions.map((u) => ({ value: u, label: u }))}
                    placeholder="Unit"
                    size="small"
                    showSearch
                    allowClear
                    style={{ width: "100%" }}
                  />
                );
              }
              return formatUnit(value);
            },
          },
          {
            title: 'Rate',
            dataIndex: 'rate',
            key: 'rate',
            width: 110,
            align: 'right',
            render: (value, record) => {
              if (isHeaderRowType(record.rowType) || record.rowType === 'description' || record.rowType === 'description-header') {
                return '';
              }
              if (!value || value === '' || value === '0' || value === '0.00') return '';
              const numValue = typeof value === 'number' ? value : parseFloat(value);
              return isNaN(numValue) ? '' : <div style={{ textAlign: 'right' }}>£{numValue.toFixed(2)}</div>;
            },
          },
          ...(showDevMode ? [{
            title: 'DB Rate',
            dataIndex: 'dbRate',
            key: 'dbRate',
            width: 100,
            align: 'right' as const,
            render: (value: string, record: any) => {
              if (isHeaderRowType(record.rowType) || record.rowType === 'description-header' || record.rowType === 'description') return '';
              if (!value || value === '') return '';
              const numValue = parseFloat(value);
              if (isNaN(numValue) || numValue === 0) return '';
              return <div style={{ textAlign: 'right', color: '#9ca3af', fontSize: '12px' }}>£{numValue.toFixed(4)}</div>;
            },
          }] : []),
          {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            align: 'right',
            render: (value, record) => {
              if (record.rowType === 'description') {
                return '';
              }

              // Show section total when collapsed for headers with children
              if ((isHeaderRowType(record.rowType) || record.rowType === 'description-header') && record.children && record.children.length > 0) {
                const isExpanded = expandedRowKeys.includes(record.key);
                if (!isExpanded && record.sectionAmount > 0) {
                  return (
                    <div style={{ textAlign: 'right' }}>
                      <span className="font-semibold">
                        £{record.sectionAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  );
                }
                return '';
              }

              // Text amount type (extra items only) — a free-typed value like
              // "Included"/"Rate Only" instead of a computed £ amount, same
              // convention DynamicBillSummaryPanel's own Amount column uses.
              if (record.amountType === 'text') {
                return <div style={{ textAlign: 'right' }}><Text>{record.amountText}</Text></div>;
              }

              if (!value || value === "0.00") return '';

              return (
                <div style={{ textAlign: 'right' }}>
                  <Text strong>
                    £{parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Text>
                </div>
              );
            },
          },
          ...(showDevMode ? [{
            title: 'SummaryKey',
            dataIndex: 'apiSummaryItem',
            key: 'summaryKey',
            width: 150,
            align: 'right' as const,
            render: (value: BoqSummaryLineItem | null | undefined, record: any) => {
              if (isHeaderRowType(record.rowType) || record.rowType === 'description-header' || record.rowType === 'description') return '';
              if (record.isExtra) {
                const node = findExtraNode(plotPavingsExtraItems?.[record.attachKey] ?? [], record.key) as DynamicBillSummaryItemNode | null;
                if (!node) return '';
                const liveAmount = parseFloat(String(record.amount ?? '0')) || 0;
                const lineItem: DynamicBillSummaryLineItem = {
                  key: node.id,
                  description: node.description,
                  quantity: node.qty,
                  unit: node.unit,
                  rate: node.rate,
                  amount: liveAmount,
                  amountType: node.amountType,
                  amountText: node.amountText,
                  resourceCode: node.resourceCode,
                  resourceCodes: node.resourceCodes,
                };
                const bc = computeDynamicBcCalculation(lineItem, (code) => codeRateLookup.get(code)?.rate ?? 0);
                // Once saved, this extra item persists into the real BOQ document's
                // `summary.plotPavings` array under its own `key`/`id` (see
                // flattenCutFillsExtraItemsToBoqLines, boqPayloadBuilder.ts) — same
                // persisted-vs-live comparison every other row's SummaryKey shows.
                // Before the first save, there's nothing to compare yet, so this
                // falls back to a live-derived "unsaved" value instead — same
                // convention DynamicBillSummaryPanel's own SummaryKey column uses.
                const persisted = apiPlotPavingsSummaryByFieldName.get(record.key);
                const persistedAmount = persisted?.amount ?? liveAmount;
                const mismatch = !!persisted && Math.abs(liveAmount - persistedAmount) > 0.01;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : persisted ? '#6b7280' : '#f59e0b' }}>
                        £{persistedAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Text>
                      <InfoCircleOutlined
                        style={{ cursor: 'pointer', color: '#3b82f6' }}
                        onClick={() => setExtraSummaryKeyRecord({ live: lineItem, persisted: persisted ?? null })}
                      />
                    </div>
                    {bc && (
                      <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
                        BC: £{bc.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Text>
                    )}
                    {!persisted && <Text style={{ fontSize: 10, color: '#f59e0b' }}>unsaved</Text>}
                  </div>
                );
              }
              if (!value) return '';
              const bcAmount = computeBcCalculation(value, (code) => codeRateLookup.get(code)?.rate ?? 0).amount;
              const liveAmount = parseFloat(String(record.amount ?? '0')) || 0;
              const mismatch = Math.abs(liveAmount - bcAmount) > 0.01;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : '#6b7280' }}>
                      £{(value.amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Text>
                    <InfoCircleOutlined
                      style={{ cursor: 'pointer', color: '#3b82f6' }}
                      onClick={() => setSummaryKeyModalRecord(value)}
                    />
                  </div>
                  <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
                    BC: £{bcAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Text>
                </div>
              );
            },
          }] : []),
          ...(dynamicSummaryEnabled && !state.isViewMode ? [{
            title: 'Actions',
            key: 'extraActions',
            width: 150,
            align: 'center' as const,
            render: (_: unknown, record: any) => {
              if (record.rowType === 'description') return '';
              if (record.isExtra) {
                const node = findExtraNode(plotPavingsExtraItems?.[record.attachKey] ?? [], record.key);
                if (!node) return '';
                if (record.nodeType === 'section') {
                  return (
                    <Space size={4}>
                      <Tooltip title={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
                        <Button
                          size="small"
                          icon={<FolderAddOutlined />}
                          title="Add Section"
                          disabled={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH}
                          onClick={() => extraItemsApi.openAddSection(record.attachKey, record.key)}
                        />
                      </Tooltip>
                      <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.attachKey, record.key)} />
                      <Button size="small" icon={<EditOutlined />} title="Edit Section" onClick={() => extraItemsApi.openEditSection(record.attachKey, node as DynamicBillSummarySectionNode)} />
                      <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey, record.key)} />
                    </Space>
                  );
                }
                return (
                  <Space size={4}>
                    <Button size="small" icon={<EditOutlined />} title="Edit Item" onClick={() => extraItemsApi.openEditItem(record.attachKey, node as DynamicBillSummaryItemNode)} />
                    <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey, record.key)} />
                  </Space>
                );
              }
              // Existing computed row — immutable, but a category (section) row
              // can host grafted extra rows. A plain computed line-item gets no
              // Actions at all — only sections are attachment points.
              if (!isHeaderRowType(record.rowType) && record.rowType !== 'description-header') {
                return '';
              }
              {
                const atMaxDepth = (rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH;
                return (
                  <Space size={4}>
                    <Tooltip title={atMaxDepth ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
                      <Button
                        size="small"
                        icon={<FolderAddOutlined />}
                        title="Add Section"
                        disabled={atMaxDepth}
                        onClick={() => extraItemsApi.openAddSection(record.key, null)}
                      />
                    </Tooltip>
                    <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.key, null)} />
                  </Space>
                );
              }
            },
          }] : []),
        ]}
        pagination={false}
        size="middle"
        bordered
        rowClassName={(record) => {
          const classes: string[] = [];
          // 'main-header' is only ever produced by newly-added extra root
          // sections (see buildRootExtraRows) — styled identically to the
          // fixed category ('sub-header') rows for visual consistency, same
          // as GroundworksCutFillBillSummary.tsx.
          if (isHeaderRowType(record.rowType)) classes.push('bg-indigo-50', 'font-semibold');
          else if (record.rowType === 'description-header') classes.push('bg-indigo-50', 'font-semibold');
          else if (record.rowType === 'description') classes.push('bg-gray-50');
          if (record.key === newlyAddedRowId) classes.push('plot-pavings-summary-row-added');
          return classes.join(' ');
        }}
        summary={(pageData) => {
          // Flatten all nested data recursively. A line-item row can now carry
          // extra grafted children (see extraItemsApi/injectExtraRows above) —
          // unlike a pure header row, its own amount still counts even though
          // it also has children, so both branches fire for that case instead
          // of being mutually exclusive.
          const flattenAll = (items: readonly any[]): any[] => {
            let result: any[] = [];
            items.forEach(item => {
              if (item.rowType === 'line-item') {
                result.push(item);
              }
              if (item.children && item.children.length > 0) {
                result.push(...flattenAll(item.children));
              }
            });
            return result;
          };

          const flattenedData = flattenAll(pageData);

          const totalAmt = flattenedData.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

          return (
            <Table.Summary.Row style={{ backgroundColor: "#fafafa", fontWeight: 700 }}>
              <Table.Summary.Cell index={0} colSpan={2}>
                <Text strong style={{ fontWeight: 700 }}>TOTAL PLOT PAVINGS</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} />
              <Table.Summary.Cell index={3} />
              <Table.Summary.Cell index={4} />
              {showDevMode && <Table.Summary.Cell index={5} />}
              <Table.Summary.Cell index={showDevMode ? 6 : 5} align="right">
                <span style={{ display: 'block', textAlign: 'right', fontWeight: 700 }}>
                  £{totalAmt.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </Table.Summary.Cell>
              {showDevMode && <Table.Summary.Cell index={7} />}
              {dynamicSummaryEnabled && !state.isViewMode && <Table.Summary.Cell index={8} />}
            </Table.Summary.Row>
          );
        }}
      />

      <style>{`
        .plot-pavings-summary-table .ant-table-thead > tr > th {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
        }
        /* Newly-added extra Section/Item row (see newlyAddedRowId) — a brief
           highlight fade so the row is easy to spot the moment its parent
           auto-expands to reveal it. */
        @keyframes plot-pavings-summary-row-added-fade {
          0% { background-color: #fef3c7; }
          100% { background-color: transparent; }
        }
        .plot-pavings-summary-row-added > td {
          animation: plot-pavings-summary-row-added-fade 1.8s ease-out;
        }
        /* Smooth expand/collapse instead of antd's default instant toggle. */
        .plot-pavings-summary-table .ant-table-row-expand-icon {
          transition: transform 0.2s ease;
        }
        .plot-pavings-summary-table .ant-table-tbody > tr {
          transition: background-color 0.3s ease;
        }
      `}</style>

      <Modal
        title="Configure Headers required"
        open={!!configViolationModalRecord}
        onCancel={() => setConfigViolationModalRecord(null)}
        footer={
          <Button type="primary" onClick={() => setConfigViolationModalRecord(null)}>
            OK
          </Button>
        }
      >
        {configViolationModalRecord && (
          <div>
            <Text strong>{configViolationModalRecord.section}</Text>
            <div style={{ marginTop: 8 }}>
              <Text>{configViolationModalRecord.message}</Text>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="SummaryKey — Amount Calculation"
        open={!!summaryKeyModalRecord}
        onCancel={() => setSummaryKeyModalRecord(null)}
        footer={null}
      >
        {summaryKeyModalRecord && (
          <BcCalculationDetails
            record={summaryKeyModalRecord}
            getDbRate={(code) => codeRateLookup.get(code)?.rate ?? 0}
          />
        )}
      </Modal>

      <Modal
        title={extraSummaryKeyRecord?.persisted ? "SummaryKey — Amount Calculation" : "SummaryKey — Live Value (not yet in BOQ export)"}
        open={!!extraSummaryKeyRecord}
        onCancel={() => setExtraSummaryKeyRecord(null)}
        footer={null}
      >
        {extraSummaryKeyRecord && (() => {
          const { live, persisted } = extraSummaryKeyRecord;
          const bc = computeDynamicBcCalculation(live, (code) => codeRateLookup.get(code)?.rate ?? 0);
          return (
            <>
              <SummaryKeyRawFields record={(persisted ?? live) as unknown as Record<string, unknown>} />
              {bc && (
                <div style={{ marginTop: 8, padding: 8, background: '#eff6ff', borderRadius: 4 }}>
                  <Text strong>BCCalculation (backend dbRate-only derivation):</Text>
                  <Descriptions bordered size="small" column={1} style={{ marginTop: 8 }}>
                    <Descriptions.Item label="Computed Rate (rounded 2dp)">£{bc.rate.toFixed(2)}</Descriptions.Item>
                    <Descriptions.Item label="Quantity (rounded)">
                      {bc.quantity.toLocaleString('en-GB')} {formatUnit(live.unit)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Amount (rounded 2dp)">
                      £{bc.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Descriptions.Item>
                  </Descriptions>
                  <div style={{ marginTop: 8 }}>
                    {bc.steps.map((step, idx) => (
                      <div key={idx}>
                        <Text style={{ fontSize: 12, color: '#374151' }}>{step}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </Modal>

      {extraItemsApi.modals}
      </div>
    )
  );
};

export default PlotPavingsBillSummary;
